import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage } from "@/lib/api";
import { TZ_OPTIONS, tzShort } from "@/lib/tz";

function empty() {
  return { nama: "", alamat: "", latitude: -6.2646, longitude: 106.8451, radius_meter: 100, timezone: "Asia/Jakarta" };
}

export default function AdminLokasi() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/master/lokasi"); setRows(data); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    const payload = {
      ...form,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      radius_meter: parseInt(form.radius_meter, 10),
    };
    try {
      if (editing) await api.put(`/master/lokasi/${editing.id}`, payload);
      else await api.post("/master/lokasi", payload);
      toast.success("Berhasil disimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }
  async function del(r) {
    if (!confirm(`Hapus lokasi ${r.nama}?`)) return;
    try { await api.delete(`/master/lokasi/${r.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  }

  function fetchGps() {
    if (!navigator.geolocation) { toast.error("GPS tidak tersedia"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setForm((f) => ({ ...f, latitude: p.coords.latitude.toFixed(6), longitude: p.coords.longitude.toFixed(6) }));
        toast.success("Koordinat perangkat diisi");
      },
      () => toast.error("Gagal mengambil GPS")
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-lokasi">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Lokasi Kerja
          </h1>
          <p className="text-slate-500 text-sm mt-1">Kelola titik lokasi, radius, dan zona waktu.</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setForm(empty()); setOpen(true); }}
          className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="lokasi-add-btn">
          <Plus size={14} /> Tambah Lokasi
        </Button>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nama</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Alamat</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Koordinat</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Radius</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Timezone</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <MapPin size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada lokasi kerja</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`lokasi-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell className="font-semibold text-slate-900">{r.nama}</TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-md">{r.alamat}</TableCell>
                    <TableCell className="font-mono-clock text-xs">{r.latitude?.toFixed?.(4)}, {r.longitude?.toFixed?.(4)}</TableCell>
                    <TableCell className="font-mono-clock text-xs">{r.radius_meter} m</TableCell>
                    <TableCell className="text-slate-700 text-sm">{tzShort(r.timezone)}<span className="text-slate-400 text-xs ml-1">({r.timezone})</span></TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => { setEditing(r); setForm({ ...r }); setOpen(true); }}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900" data-testid={`lokasi-edit-${r.id}`}>
                        <Pencil size={13} /> Edit
                      </button>
                      {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="lokasi-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Lokasi" : "Tambah Lokasi"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Nama Lokasi</Label><Input className="mt-1.5" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="lokasi-input-nama" /></div>
            <div className="sm:col-span-2"><Label>Alamat</Label><Textarea className="mt-1.5" value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} data-testid="lokasi-input-alamat" /></div>
            <div><Label>Latitude</Label><Input className="mt-1.5" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="lokasi-input-lat" /></div>
            <div><Label>Longitude</Label><Input className="mt-1.5" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="lokasi-input-lng" /></div>
            <div><Label>Radius (meter)</Label><Input type="number" className="mt-1.5" value={form.radius_meter} onChange={(e) => setForm({ ...form, radius_meter: e.target.value })} data-testid="lokasi-input-radius" /></div>
            <div>
              <Label>Timezone</Label>
              <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                <SelectTrigger className="mt-1.5" data-testid="lokasi-input-tz"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TZ_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={fetchGps} className="w-full" data-testid="lokasi-btn-use-gps">
                Gunakan Lokasi Perangkat Saat Ini
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={save} data-testid="lokasi-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
